import React from "react";

import { faBuilding } from "@fortawesome/pro-light-svg-icons/faBuilding";
import Item from "antd/es/descriptions/Item";
import Tag from "antd/es/tag";

import { Person } from "@mapmycustomers/shared/types/entity";

import { Email, PhoneNumber } from "@app/component/field";
import i18nService from "@app/config/I18nService";
import { formatRawDate } from "@app/util/formatters";

import BaseInfoWindow from "./BaseInfoWindow";

interface Props {
  callbackName?: string;
  person: Person;
}

const PersonInfoWindow: React.FC<Props> = ({ callbackName, person }) => {
  // not using useIntl because this component is rendered separately from the main component tree
  const intl = i18nService.getIntl();

  if (!intl) {
    return null;
  }

  return (
    <BaseInfoWindow
      footer={
        <div
          dangerouslySetInnerHTML={{
            __html: `
          <button
            class="ant-btn ant-btn-primary ant-btn-block"
            onclick="${callbackName}()"
            type="button"
          >
            ${intl.formatMessage({
              id: "simpleEntityMap.infoWindow.person.preview",
              defaultMessage: "Preview Person",
              description:
                "Preview Person button in standard info window for People in a Simple Entity Map",
            })}
          </button>
        `,
          }}
        />
      }
      title={person.name}
      titleIcon={faBuilding}
    >
      {person.outOfCadence ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.person.lastActivityDate",
            defaultMessage: "Last Activity date",
            description:
              "Last Activity date field name in standard info window for People in a Simple Entity Map",
          })}
        >
          <Tag color="error">
            {person.lastCompletedActivity
              ? formatRawDate(person.lastCompletedActivity.completedAt, "PPp")
              : intl.formatMessage({
                  id: "simpleEntityMap.infoWindow.person.lastActivityDate.never",
                  defaultMessage: "No activity yet",
                  description:
                    "Last Activity date field name in standard info window for People in a Simple Entity Map",
                })}
          </Tag>
        </Item>
      ) : null}
      {person.address ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.person.address",
            defaultMessage: "Address",
            description:
              "Address field name in standard info window for People in a Simple Entity Map",
          })}
        >
          {person.address}
        </Item>
      ) : null}
      {person.city ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.person.city",
            defaultMessage: "City",
            description:
              "City field name in standard info window for People in a Simple Entity Map",
          })}
        >
          {person.city}
        </Item>
      ) : null}
      {person.region ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.person.state",
            defaultMessage: "State",
            description:
              "State field name in standard info window for People in a Simple Entity Map",
          })}
        >
          {person.region}
        </Item>
      ) : null}
      {person.phone ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.person.phone",
            defaultMessage: "Phone",
            description:
              "Phone field name in standard info window for People in a Simple Entity Map",
          })}
        >
          <PhoneNumber value={person.phone} />
        </Item>
      ) : null}
      {person.email ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.person.email",
            defaultMessage: "Email",
            description:
              "Email field name in standard info window for People in a Simple Entity Map",
          })}
        >
          <Email entity={person} value={person.email} />
        </Item>
      ) : null}
    </BaseInfoWindow>
  );
};

export default PersonInfoWindow;
